// Unified navigation chrome (User Journeys & Service Design v2 — the
// dark-navy left rail shared across HR, Manager, and Employee desktop).
//
//   AppSidebar     — fixed dark rail (lg+) driven by routes (NavLink).
//                    Used by the HR / Manager / Admin Shell.
//   SectionSidebar — same rail driven by in-page section state (buttons).
//                    Used by the Employee portal, whose "sections" are
//                    local state, not routes (§9.2 calm one-section view).
//   MobileTopNav   — compact top bar + scrollable pill nav (below lg) so
//                    every route stays reachable on a phone.
//
// Surface-agnostic: `bg-primary` / `text-primary` follow the ambient
// `data-surface` token switch, so the same rail renders teal on HR/Manager
// and Employee-blue inside a `data-surface="employee"` subtree.

import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import { Logo } from '@/components/brand/Logo.tsx';
import { LifebuoyIcon } from '@/components/ui/icons.tsx';

export interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  end?: boolean;
  /**
   * Override NavLink's own prefix-match `isActive`. Needed when a sibling
   * item's path is itself a sub-path of this one (e.g. "Support pathways" →
   * `/hr/cases` vs. "New support pathway" → `/hr/cases/new`) — without this,
   * `end: false` lights up
   * both simultaneously since NavLink only knows string prefixes, not the
   * route tree.
   */
  isActive?: (pathname: string) => boolean;
}

export interface SectionNavItem<Id extends string = string> {
  id: Id;
  label: string;
  icon: ReactNode;
}

interface SidebarChromeProps {
  eyebrow: string;
  user: { name: string; roleLabel: string };
  onSignOut: () => void;
  signingOut?: boolean;
  helpHref?: string;
}

function initialsOf(name: string, fallback: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback.slice(0, 2).toUpperCase();
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

const ACTIVE_LINK = 'bg-primary/20 text-white [&_svg]:text-primary';
const INACTIVE_LINK = 'text-white/70 hover:bg-white/5 hover:text-white [&_svg]:text-white/50';
const LINK_BASE =
  'flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm font-medium transition';

// The dark-rail shell: logo → eyebrow → nav slot → user pill + footer.
// Both sidebar variants render their nav list into `children`.
function SidebarFrame({
  eyebrow,
  user,
  onSignOut,
  signingOut,
  helpHref,
  children,
}: SidebarChromeProps & { children: ReactNode }) {
  return (
    <aside className="hidden w-64 shrink-0 flex-col bg-ink text-white lg:sticky lg:top-0 lg:flex lg:h-dvh">
      <div className="px-6 py-6">
        <Logo onDark hideMark />
      </div>
      <p className="px-6 pb-2 text-[0.68rem] font-semibold tracking-[0.18em] text-white/40 uppercase">
        {eyebrow}
      </p>
      <nav aria-label={eyebrow} className="flex-1 space-y-1 overflow-y-auto px-3 py-1">
        {children}
      </nav>
      <div className="space-y-3 border-t border-white/10 p-4">
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="grid size-9 shrink-0 place-items-center rounded-full bg-primary text-xs font-semibold text-primary-foreground"
          >
            {initialsOf(user.name, user.roleLabel)}
          </span>
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="truncate text-sm font-medium text-white">
              {user.name || user.roleLabel}
            </span>
            <span className="truncate text-xs text-white/50">{user.roleLabel}</span>
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          {helpHref ? (
            <a
              href={helpHref}
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-white/60 transition hover:text-white [&_svg]:size-4"
            >
              <LifebuoyIcon />
              Need help?
            </a>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={onSignOut}
            disabled={signingOut}
            className="rounded-md px-2 py-1.5 text-xs text-white/60 transition hover:text-white disabled:opacity-60"
          >
            {signingOut ? '…' : 'Sign out'}
          </button>
        </div>
      </div>
    </aside>
  );
}

/** Route-driven dark rail (lg+). */
export function AppSidebar({ nav, ...chrome }: SidebarChromeProps & { nav: NavItem[] }) {
  const location = useLocation();
  return (
    <SidebarFrame {...chrome}>
      {nav.map((item) => {
        const active = item.isActive?.(location.pathname);
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end ?? false}
            className={({ isActive }) =>
              [LINK_BASE, (active ?? isActive) ? ACTIVE_LINK : INACTIVE_LINK].join(' ')
            }
          >
            {item.icon}
            <span>{item.label}</span>
          </NavLink>
        );
      })}
    </SidebarFrame>
  );
}

/** Section-state-driven dark rail (lg+) for the Employee portal. */
export function SectionSidebar<Id extends string>({
  items,
  active,
  onSelect,
  ...chrome
}: SidebarChromeProps & {
  items: readonly SectionNavItem<Id>[];
  active: Id;
  onSelect: (id: Id) => void;
}) {
  return (
    <SidebarFrame {...chrome}>
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onSelect(item.id)}
          aria-current={active === item.id ? 'page' : undefined}
          className={[LINK_BASE, active === item.id ? ACTIVE_LINK : INACTIVE_LINK].join(' ')}
        >
          {item.icon}
          <span>{item.label}</span>
        </button>
      ))}
    </SidebarFrame>
  );
}

function topLinkClass(active: boolean): string {
  return [
    'inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm whitespace-nowrap transition',
    active
      ? 'bg-primary/15 text-foreground [&_svg]:text-primary'
      : 'text-muted-foreground hover:text-foreground [&_svg]:text-muted-foreground/70',
  ].join(' ');
}

/** Compact top bar + scrollable pill nav. Rendered below `lg`. */
export function MobileTopNav({
  eyebrow,
  nav,
  onSignOut,
  signingOut,
}: SidebarChromeProps & { nav: NavItem[] }) {
  const location = useLocation();
  return (
    <header className="border-b border-border bg-card lg:hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <Logo />
        <div className="flex items-center gap-3">
          <span className="text-[0.68rem] font-semibold tracking-[0.16em] text-muted-foreground uppercase">
            {eyebrow}
          </span>
          <button
            type="button"
            onClick={onSignOut}
            disabled={signingOut}
            className="text-sm text-muted-foreground transition hover:text-foreground disabled:opacity-60"
          >
            {signingOut ? '…' : 'Sign out'}
          </button>
        </div>
      </div>
      <nav aria-label={eyebrow} className="flex gap-1 overflow-x-auto px-2 pb-2 [&_svg]:size-4">
        {nav.map((item) => {
          const active = item.isActive?.(location.pathname);
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end ?? false}
              className={({ isActive }) => topLinkClass(active ?? isActive)}
            >
              {item.icon}
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </header>
  );
}
